/**
 * Lifestyle Carousel
 * - Center-active card: büyür, video başlar (sessiz)
 * - Mouse drag (desktop) + touch swipe (mobile)
 * - Dot navigation
 * - Opsiyonel autoplay
 */

class LifestyleCarousel {
  constructor(section) {
    this.section = section;
    this.track = section.querySelector('.lc-track');
    this.slides = Array.from(section.querySelectorAll('.lc-slide'));
    this.dots = Array.from(section.querySelectorAll('.lc-dot'));
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.baseOffset = 0;
    this.threshold = 60;

    this.inactiveScale = parseFloat(this.track.dataset.inactiveScale || '0.82');
    this.autoplay = section.dataset.autoplay === 'true';
    this.autoplaySpeed = (parseInt(section.dataset.autoplaySpeed) || 4) * 1000;
    this.isMuted = true;

    const initialIndex = parseInt(this.track.dataset.initialIndex);
    this.current = Number.isFinite(initialIndex)
      ? initialIndex
      : Math.floor(this.slides.length / 2);

    this.init();
  }

  init() {
    if (!this.track || this.slides.length === 0) return;

    this.applyInactiveScale();
    this.updateLayout(false);
    this.bindDrag();
    this.bindDots();
    this.bindResize();
    this.bindSlideClick();
    this.bindSoundToggle();
    this.playVideo(this.current);
    if (this.autoplay) this.startAutoplay();
  }

  /* ── Inactive scale from schema ─────────────────────────── */
  applyInactiveScale() {
    this.slides.forEach((slide) => {
      if (!slide.classList.contains('is-active')) {
        slide.style.transform = `scale(${this.inactiveScale})`;
      }
    });
  }

  /* ── Go to slide ────────────────────────────────────────── */
  goTo(index, animate = true) {
    if (index < 0) index = 0;
    if (index >= this.slides.length) index = this.slides.length - 1;

    this.pauseVideo(this.current);

    this.current = index;
    this.updateLayout(animate);
    this.playVideo(this.current);
    this.updateDots();

    this.baseOffset = this.getTargetOffset();
  }

  /* ── Calculate offset to center active slide ────────────── */
  getTargetOffset() {
    const trackWrapper = this.section.querySelector('.lc-track-wrapper');
    const wrapperWidth = trackWrapper.offsetWidth;
    const activeSlide = this.slides[this.current];
    const slideLeft = activeSlide.offsetLeft;
    const slideWidth = activeSlide.offsetWidth;
    return wrapperWidth / 2 - slideLeft - slideWidth / 2;
  }

  /* ── Apply translate + active class ────────────────────── */
  updateLayout(animate = true) {
    const offset = this.getTargetOffset();
    this.track.style.transition = animate
      ? 'transform 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      : 'none';
    this.track.style.transform = `translateX(${offset}px)`;

    this.applyActiveStates();

    this.baseOffset = offset;
  }

  /* ── Active class + scale only (no track movement) ──────── */
  applyActiveStates() {
    this.slides.forEach((slide, i) => {
      const isActive = i === this.current;
      slide.classList.toggle('is-active', isActive);
      slide.style.transform = isActive ? 'scale(1)' : `scale(${this.inactiveScale})`;
    });
  }

  /* ── Find slide whose center is closest to wrapper center ─ */
  getNearestSlideIndex(currentOffset) {
    const wrapper = this.section.querySelector('.lc-track-wrapper');
    const wrapperCenter = wrapper.offsetWidth / 2;
    let nearest = this.current;
    let minDist = Infinity;
    this.slides.forEach((slide, i) => {
      const slideCenter = currentOffset + slide.offsetLeft + slide.offsetWidth / 2;
      const dist = Math.abs(slideCenter - wrapperCenter);
      if (dist < minDist) {
        minDist = dist;
        nearest = i;
      }
    });
    return nearest;
  }

  /* ── Video helpers ──────────────────────────────────────── */
  playVideo(index) {
    const slide = this.slides[index];
    if (!slide || slide.dataset.mediaType !== 'video') return;
    const video = slide.querySelector('.lc-video');
    const poster = slide.querySelector('.lc-video-poster');
    if (!video) return;
    video.muted = this.isMuted;
    if (video.preload === 'none') video.preload = 'auto';
    const playPromise = video.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise
        .then(() => {
          if (poster) poster.classList.add('is-playing');
        })
        .catch(() => {
          if (!this.isMuted) {
            video.muted = true;
            video.play().then(() => {
              if (poster) poster.classList.add('is-playing');
            }).catch(() => {});
          }
        });
    }
  }

  pauseVideo(index) {
    const slide = this.slides[index];
    if (!slide || slide.dataset.mediaType !== 'video') return;
    const video = slide.querySelector('.lc-video');
    if (!video) return;
    video.pause();
  }

  /* ── Drag (mouse + touch) ───────────────────────────────── */
  bindDrag() {
    const wrapper = this.section.querySelector('.lc-track-wrapper');
    if (!wrapper) return;

    wrapper.addEventListener('mousedown', (e) => {
      this.onDragStart(e.pageX);
      e.preventDefault();
    });
    window.addEventListener('mousemove', (e) => {
      if (this.isDragging) this.onDragMove(e.pageX);
    });
    window.addEventListener('mouseup', (e) => {
      if (this.isDragging) this.onDragEnd(e.pageX);
    });

    wrapper.addEventListener(
      'touchstart',
      (e) => {
        this.onDragStart(e.touches[0].pageX);
      },
      { passive: true }
    );
    wrapper.addEventListener(
      'touchmove',
      (e) => {
        if (this.isDragging) {
          e.preventDefault();
          this.onDragMove(e.touches[0].pageX);
        }
      },
      { passive: false }
    );
    wrapper.addEventListener('touchend', (e) => {
      if (this.isDragging) this.onDragEnd(e.changedTouches[0].pageX);
    });
  }

  onDragStart(x) {
    this.isDragging = true;
    this.startX = x;
    this.track.style.transition = 'none';
    if (this.autoplayTimer) clearInterval(this.autoplayTimer);
  }

  onDragMove(x) {
    const delta = x - this.startX;
    const newOffset = this.baseOffset + delta;
    this.track.style.transform = `translateX(${newOffset}px)`;

    const nearest = this.getNearestSlideIndex(newOffset);
    if (nearest !== this.current) {
      this.pauseVideo(this.current);
      this.current = nearest;
      this.applyActiveStates();
      this.updateDots();
      this.playVideo(this.current);
    }
  }

  onDragEnd() {
    this.isDragging = false;
    this.goTo(this.current);
    if (this.autoplay) this.startAutoplay();
  }

  /* ── Click on side slide jumps to it ────────────────────── */
  bindSlideClick() {
    this.slides.forEach((slide, i) => {
      slide.addEventListener('click', (e) => {
        if (e.target.closest('.lc-sound-toggle')) return;
        if (i === this.current) return;
        const moved = Math.abs(this.startX - (e.pageX || 0));
        if (moved > 10) return;
        this.goTo(i);
      });
    });
  }

  /* ── Sound toggle (mute/unmute) ─────────────────────────── */
  bindSoundToggle() {
    const buttons = this.section.querySelectorAll('.lc-sound-toggle');
    buttons.forEach((btn) => {
      btn.addEventListener('mousedown', (e) => e.stopPropagation());
      btn.addEventListener('touchstart', (e) => e.stopPropagation(), { passive: true });
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.isMuted = !this.isMuted;
        const activeSlide = this.slides[this.current];
        const video = activeSlide ? activeSlide.querySelector('.lc-video') : null;
        if (video) {
          video.muted = this.isMuted;
          if (video.paused) video.play().catch(() => {});
        }
        this.updateSoundToggleUI();
      });
    });
    this.updateSoundToggleUI();
  }

  updateSoundToggleUI() {
    const buttons = this.section.querySelectorAll('.lc-sound-toggle');
    buttons.forEach((btn) => {
      btn.classList.toggle('is-muted', this.isMuted);
      btn.setAttribute('aria-label', this.isMuted ? 'Sesi aç' : 'Sesi kapat');
    });
  }

  /* ── Dots ───────────────────────────────────────────────── */
  bindDots() {
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(i));
    });
  }

  updateDots() {
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.current);
    });
  }

  /* ── Autoplay ───────────────────────────────────────────── */
  startAutoplay() {
    if (this.autoplayTimer) clearInterval(this.autoplayTimer);
    this.autoplayTimer = setInterval(() => {
      const next = (this.current + 1) % this.slides.length;
      this.goTo(next);
    }, this.autoplaySpeed);
  }

  /* ── Resize ─────────────────────────────────────────────── */
  bindResize() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.goTo(this.current, false);
      }, 120);
    });
  }
}

/* ── Init all carousel sections ─────────────────────────── */
function initLifestyleCarousels() {
  document.querySelectorAll('.lifestyle-carousel-section[data-section-id]').forEach((section) => {
    if (!section.dataset.lcInit) {
      new LifestyleCarousel(section);
      section.dataset.lcInit = 'true';
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLifestyleCarousels);
} else {
  initLifestyleCarousels();
}

document.addEventListener('shopify:section:load', (e) => {
  const section = e.target.querySelector('.lifestyle-carousel-section');
  if (section && !section.dataset.lcInit) {
    new LifestyleCarousel(section);
    section.dataset.lcInit = 'true';
  }
});
